import { type UseModalState } from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import useSendbirdStateContextHook from "@sendbird/uikit-react/useSendbirdStateContext";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import {
  type ConfirmAttendancePayload,
  useConfirmAttendance,
} from "@src/appV2/Shifts/UrgentShifts/api/useConfirmAttendance";
import { AttendanceConfirmationChannel } from "@src/appV2/Shifts/UrgentShifts/constant";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { formatISO, parseISO } from "date-fns";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { generatePath, useHistory } from "react-router-dom";
import { z } from "zod";

import { SHIFT_DETAILS_UPDATE_ETA_CONFIRMED_FULL_PATH } from "../../../WorkerShifts/path";
import { invalidateWorkerTrips } from "../useGetWorkerTrips";
import { UpdateEtaBottomSheet } from "./BottomSheet";
import { getAutoMessageToFacility } from "./utils/getAutoMessageToFacility";
import { sendMessageToFacility } from "./utils/sendMessageToWorkplace";

const updateEtaSchema = z.object({
  arrivalTimeIso: z.string().min(1, "Please select an arrival time"),
  message: z.string(),
});

type UpdateEtaFormValues = z.infer<typeof updateEtaSchema>;

export interface UpdateEtaBottomSheetFormProps {
  modalState: UseModalState;
  shiftId: string;
  workplaceId: string;
  workplaceTimezone: string;
  shiftStartTime: Date;
  etaUpdateAvailableTimes: Date[];
}

export function UpdateEtaBottomSheetForm(props: UpdateEtaBottomSheetFormProps) {
  const {
    modalState,
    shiftId,
    workplaceId,
    workplaceTimezone,
    shiftStartTime,
    etaUpdateAvailableTimes,
  } = props;

  const history = useHistory();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const sendBirdStateContext = useSendbirdStateContextHook();
  const worker = useDefinedWorker();

  const { mutateAsync: confirmAttendance, isLoading: isConfirmAttendanceLoading } =
    useConfirmAttendance();

  const initialEta = etaUpdateAvailableTimes[0];
  const initialArrivalTimeIso = formatISO(initialEta);
  const initialMessage = getAutoMessageToFacility({
    selectedEta: initialEta,
    shiftStartTime,
    workplaceTimezone,
  });

  const { handleSubmit, setValue, watch } = useForm<UpdateEtaFormValues>({
    resolver: zodResolver(updateEtaSchema),
    defaultValues: {
      arrivalTimeIso: initialArrivalTimeIso,
      message: initialMessage,
    },
  });

  const arrivalTimeIso = watch("arrivalTimeIso");
  const message = watch("message");

  const selectedEta = useMemo(() => parseISO(arrivalTimeIso), [arrivalTimeIso]);

  const onSubmit = handleSubmit(async (data: UpdateEtaFormValues) => {
    const selectedTime = parseISO(data.arrivalTimeIso);

    const trimmedMessage = data.message.trim();
    const messageToFacility =
      trimmedMessage.length > 0
        ? trimmedMessage
        : getAutoMessageToFacility({
            selectedEta: selectedTime,
            shiftStartTime,
            workplaceTimezone,
          });

    const payload: ConfirmAttendancePayload = {
      shiftId,
      eta: data.arrivalTimeIso,
      channel: AttendanceConfirmationChannel.MY_SHIFT_PAGE,
      workplaceId,
    };

    try {
      await confirmAttendance(payload);

      await sendMessageToFacility({
        messageToFacility,
        sendBirdStateContext,
        workplaceId,
        workerId: worker.userId,
      });

      await invalidateWorkerTrips(queryClient, worker.userId, shiftId);

      history.replace(
        generatePath(SHIFT_DETAILS_UPDATE_ETA_CONFIRMED_FULL_PATH, {
          shiftId,
        }),
        {
          updatedEta: selectedTime.toISOString(),
          workplaceTimezone,
        }
      );
    } catch (error) {
      showErrorToast("Something went wrong while updating your ETA. Please try again.");
      logError(APP_V2_APP_EVENTS.UPDATE_ETA_BOTTOM_SHEET_ERROR, {
        error,
        metadata: {
          shiftId,
          workplaceId,
        },
      });
    }
  });

  return (
    <UpdateEtaBottomSheet
      modalState={modalState}
      isSubmitting={isConfirmAttendanceLoading}
      shiftStartTime={shiftStartTime}
      selectedEta={selectedEta}
      workplaceTimezone={workplaceTimezone}
      etaUpdateAvailableTimes={etaUpdateAvailableTimes}
      message={message}
      onEtaChange={(eta: Date): void => {
        setValue("arrivalTimeIso", formatISO(eta));
        setValue(
          "message",
          getAutoMessageToFacility({
            selectedEta: eta,
            shiftStartTime,
            workplaceTimezone,
          })
        );
      }}
      onMessageChange={(nextMessage) => {
        setValue("message", nextMessage);
      }}
      onSubmit={async () => {
        await onSubmit();
      }}
    />
  );
}
